import React from "react";
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
} from "@tanstack/react-query";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { RequireAuth } from "./components/auth";
import { AuthProvider } from "./contexts/AuthContext";
import { AppProvider } from "./contexts/AppContext";
import { CatalogProvider } from "./contexts/CatalogContext";
import { SidebarProvider } from "./contexts/SidebarContext";
import { ThemeProvider } from "./contexts/ThemeContext";

import AttributeReview from "./pages/AttributeReview";
import ApprovalPipelineJobs from "./pages/ApprovalPipelineJobs";
import CatalogOverview from "./pages/CatalogOverview";
import CompareMarketplaces from "./pages/CompareMarketplaces";
import Dashboard from "./pages/Dashboard";
import ForgotPassword from "./pages/ForgotPassword";
import InviteAccept from "./pages/InviteAccept";
import Leaves from "./pages/Leaves";
import Login from "./pages/Login";
import MappingReview from "./pages/MappingReview";
import Marketplaces from "./pages/Marketplaces";
import MarketplaceSync from "./pages/MarketplaceSync";
import MasterSKUs from "./pages/MasterSKUs";
import Orders from "./pages/Orders";
import SellerDetail from "./pages/SellerDetail";
import Sellers from "./pages/Sellers";
import ValueMapping from "./pages/ValueMapping";
import Settings from "./pages/Settings";
import SettingsAmazonDone from "./pages/SettingsAmazonDone";

import "./App.css";

import { showToast } from "./components/ui";
import { isSessionExpiryError } from "./lib/sessionExpiry";

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      // Avoid showing toast for session expiry as it redirects
      if (!isSessionExpiryError(error)) {
        showToast.error(
          "Network Error",
          (error as Error).message || "Failed to load data. Please try again.",
        );
      }
    },
  }),
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const basename = process.env.REACT_APP_BASENAME || "/";

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <SidebarProvider>
          <AuthProvider>
            <AppProvider>
              <CatalogProvider>
                <Router basename={basename}>
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route
                      path="/forgot-password"
                      element={<ForgotPassword />}
                    />
                    <Route path="/invite/accept" element={<InviteAccept />} />

                    <Route
                      path="/catalog"
                      element={
                        <RequireAuth>
                          <CatalogOverview />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/catalog/sync/:marketplaceCode?"
                      element={
                        <RequireAuth>
                          <MarketplaceSync />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/catalog/compare"
                      element={
                        <RequireAuth>
                          <CompareMarketplaces />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/catalog/master-skus"
                      element={
                        <RequireAuth>
                          <MasterSKUs />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/catalog/mapping"
                      element={
                        <RequireAuth>
                          <AttributeReview />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/catalog/leaves"
                      element={
                        <RequireAuth>
                          <Leaves />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/catalog/mapping/jobs"
                      element={
                        <RequireAuth>
                          <ApprovalPipelineJobs />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/catalog/mapping/values"
                      element={
                        <RequireAuth>
                          <ValueMapping />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/catalog/mapping-review"
                      element={
                        <RequireAuth>
                          <MappingReview />
                        </RequireAuth>
                      }
                    />

                    <Route
                      path="/sellers"
                      element={
                        <RequireAuth>
                          <Sellers />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/sellers/:id"
                      element={
                        <RequireAuth>
                          <SellerDetail />
                        </RequireAuth>
                      }
                    />

                    <Route
                      path="/orders"
                      element={
                        <RequireAuth>
                          <Orders />
                        </RequireAuth>
                      }
                    />

                    <Route
                      path="/"
                      element={
                        <RequireAuth>
                          <Dashboard />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/performance"
                      element={
                        <RequireAuth>
                          <Dashboard />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/disputes"
                      element={
                        <RequireAuth>
                          <Dashboard />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/sync"
                      element={
                        <RequireAuth>
                          <Dashboard />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/notifications"
                      element={
                        <RequireAuth>
                          <Dashboard />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/audit"
                      element={
                        <RequireAuth>
                          <Dashboard />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/admin-users"
                      element={
                        <RequireAuth>
                          <Dashboard />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/marketplaces"
                      element={
                        <RequireAuth>
                          <Marketplaces />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <RequireAuth>
                          <Settings />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/settings/amazon/done"
                      element={<SettingsAmazonDone />}
                    />
                  </Routes>
                </Router>
              </CatalogProvider>
            </AppProvider>
          </AuthProvider>
        </SidebarProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
